import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { CATEGORIES } from '../../constants/categories';

const GridCarousel = () => {
    const navigate = useNavigate();

    return (
        <Wrapper>
            {/* ── Section Header ── */}
            <Header>
                <div className="h-left">
                    <span className="h-eyebrow">What are you looking for?</span>
                    <h4 className="h-title">Shop by Category</h4>
                </div>
                <button className="h-view-all" onClick={() => navigate('/categories')}>
                    View All &nbsp;<i className="fa fa-arrow-right" />
                </button>
            </Header>

            {/* ── Category Grid ── */}
            <Grid>
                {CATEGORIES.map((cat, i) => (
                    <Card
                        key={i}
                        $gradient={cat.gradient}
                        $iconBg={cat.iconBg}
                        onClick={() => navigate(`/category/${encodeURIComponent(cat.name)}`)}
                        aria-label={cat.name}
                    >
                        {/* Frosted tag pill */}
                        <span className="cat-tag">{cat.tag}</span>

                        {/* Circular image bubble */}
                        <div className="cat-img-wrap">
                            <img src={cat.img} alt={cat.name} loading="lazy" />
                        </div>

                        {/* Name + sub-label */}
                        <div className="cat-text">
                            <h6 className="cat-name">{cat.name}</h6>
                            <p className="cat-sub">{cat.sub}</p>
                        </div>

                        {/* Shop Now CTA */}
                        <div className="cat-footer">
                            <span className="cat-cta">
                                Shop Now <i className="fa fa-arrow-right" />
                            </span>
                        </div>
                    </Card>
                ))}
            </Grid>
        </Wrapper>
    );
};

export default GridCarousel;

/* ─── Styled components ───────────────────── */

const Wrapper = styled.section`
    width: 100%;
`;

const Header = styled.div`
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    margin-bottom: 28px;
    flex-wrap: wrap;
    gap: 12px;

    .h-left {
        display: flex;
        flex-direction: column;
        gap: 4px;
    }

    .h-eyebrow {
        font-size: 12px;
        font-weight: 600;
        color: #2563eb;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        font-family: 'Poppins', sans-serif;
    }

    .h-title {
        font-size: 22px;
        font-weight: 700;
        color: #0f172a;
        margin: 0;
        font-family: 'Poppins', sans-serif;
        letter-spacing: -0.02em;
    }

    .h-view-all {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-size: 13px;
        font-weight: 600;
        color: #2563eb;
        background: #eff6ff;
        border: 1.5px solid #bfdbfe;
        padding: 8px 18px;
        border-radius: 24px;
        cursor: pointer;
        transition: background 0.2s, color 0.2s, border-color 0.2s;
        font-family: 'Poppins', sans-serif;
        white-space: nowrap;

        &:hover {
            background: #2563eb;
            color: #fff;
            border-color: #2563eb;
        }
    }
`;

const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(8, 1fr);
    gap: 14px;

    @media (max-width: 1200px) { grid-template-columns: repeat(4, 1fr); }
    @media (max-width: 768px)  { grid-template-columns: repeat(3, 1fr); gap: 12px; }
    @media (max-width: 480px)  { grid-template-columns: repeat(2, 1fr); gap: 10px; }
`;

const Card = styled.div`
    background: ${({ $gradient }) => $gradient};
    border-radius: 16px;
    border: 1px solid rgba(255,255,255,0.8);
    padding: 16px 12px 14px;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    position: relative;
    text-align: center;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
    transition: transform 0.28s cubic-bezier(0.34,1.56,0.64,1),
                box-shadow 0.28s ease;

    /* subtle top-edge shine */
    &::before {
        content: '';
        position: absolute;
        top: 0; left: 10%; right: 10%;
        height: 1px;
        background: rgba(255,255,255,0.9);
        border-radius: 1px;
    }

    &:hover {
        transform: translateY(-6px);
        box-shadow: 0 14px 32px rgba(0,0,0,0.10);
    }

    /* ── Frosted tag pill ── */
    .cat-tag {
        position: absolute;
        top: 10px; right: 10px;
        font-size: 8.5px;
        font-weight: 700;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: #475569;
        background: rgba(255,255,255,0.72);
        padding: 2px 7px;
        border-radius: 20px;
        backdrop-filter: blur(4px);
        line-height: 1.6;
    }

    /* ── Circular image bubble ── */
    .cat-img-wrap {
        width: 80px;
        height: 80px;
        border-radius: 50%;
        background: ${({ $iconBg }) => $iconBg};
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        flex-shrink: 0;
        box-shadow: 0 4px 12px rgba(0,0,0,0.08);
        transition: transform 0.3s ease;
    }

    &:hover .cat-img-wrap {
        transform: scale(1.08);
    }

    .cat-img-wrap img {
        width: 72%;
        height: 72%;
        object-fit: contain;
    }

    /* ── Text ── */
    .cat-text {
        display: flex;
        flex-direction: column;
        gap: 2px;
        width: 100%;
    }

    .cat-name {
        font-size: 12.5px;
        font-weight: 700;
        color: #0f172a;
        margin: 0;
        line-height: 1.35;
        font-family: 'Poppins', sans-serif;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }

    .cat-sub {
        font-size: 10px;
        color: #64748b;
        margin: 0;
        font-weight: 400;
        display: -webkit-box;
        -webkit-line-clamp: 1;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }

    /* ── Shop Now CTA ── */
    .cat-footer {
        width: 100%;
        padding-top: 8px;
        border-top: 1px solid rgba(0,0,0,0.06);
        margin-top: auto;
    }

    .cat-cta {
        font-size: 11px;
        font-weight: 600;
        color: #2563eb;
        font-family: 'Poppins', sans-serif;
        display: inline-flex;
        align-items: center;
        gap: 4px;

        i { font-size: 9px; transition: transform 0.2s ease; }
    }

    &:hover .cat-cta i {
        transform: translateX(3px);
    }

    /* ── Responsive ── */
    @media (max-width: 768px) {
        padding: 14px 10px 12px;
        .cat-img-wrap { width: 64px; height: 64px; }
        .cat-name { font-size: 11.5px; }
    }

    @media (max-width: 480px) {
        border-radius: 12px;
        gap: 8px;
        .cat-img-wrap { width: 56px; height: 56px; }
        .cat-name  { font-size: 11px; }
        .cat-sub   { display: none; }
        .cat-footer { display: none; }
    }
`;
